<template :key="$route.fullPath">
  <div class="financial-product-detail-component">
    <v-expansion-panel
      v-model="panel"
      style="margin-top: 8px!important;"
      expand
    >
      <!-- Information start -->
      <v-expansion-panel-content>
        <v-alert
          v-if="error"
          type="error"
          :value="true"
          class="mt-2"
        >
          {{ error }}
        </v-alert>
        <v-progress-linear
          v-if="switching"
          :color="colorTheme"
          :indeterminate="true"
          class="ma-0"
        />
        <template v-slot:header>
          <h6 class="title">
            <v-icon
              class="mr-2"
            >
              domain
            </v-icon>
            {{ type | capitalize }} Information |
            Instrument: {{ financialProduct.instrument }} |
            Stage: {{ financialProduct.stage }}
          </h6>
        </template>
        <v-layout
          class="px-3"
        >
          <stage-transition-buttons
            :type="type"
            :unsaved-data="unsavedData"
            :financial-product="financialProduct"
            :on-custom-field-change="addOrUpdateCustomField"
            :get-custom-field-value="getCustomFieldValueByName"
            :color-theme="colorTheme"
            :error="error"
            :on-stage-transition-confirmed="onStageTransition"
          />
          <v-btn
            v-if="financialProduct.stage !== 'declined'"
            style="color: white; float:right"
            :color="colorTheme"
            :disabled="!unsavedData"
            @click="saveFinancialProductData"
          >
            <v-icon medium>
              save
            </v-icon>
            &nbsp; SAVE DATA
          </v-btn>
        </v-layout>
        <v-card>
          <v-container
            fluid
            grid-list-sm
          >
            <v-layout
              row
              wrap
            >
              <v-flex
                xs4
                sm2
              >
                <v-text-field
                  v-model="financialProduct.id"
                  label="Id"
                  class="is-readonly"
                  :color="colorTheme"
                  readonly
                />
              </v-flex>
              <v-flex
                xs4
                sm2
              >
                <v-text-field
                  v-model="financialProduct.owner_id"
                  label="Owner Id"
                  :color="colorTheme"
                  class="is-readonly"
                  readonly
                />
              </v-flex>
              <v-flex
                xs4
                sm2
              >
                <v-text-field
                  v-model="financialProduct.security_id"
                  label="Security Id"
                  class="is-readonly"
                  :color="colorTheme"
                  readonly
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs4
                sm2
              >
                <v-select
                  v-model="financialProduct.security_id_type"
                  :items="securityIdTypes"
                  readonly
                  class="is-readonly"
                  item-text="name"
                  :color="colorTheme"
                  item-value="value"
                  label="Security Id Type"
                  @change="(val) => onChange(val, 'security_id_type')"
                />
              </v-flex>
              <v-flex
                xs4
                sm2
              >
                <v-select
                  v-model="financialProduct.regulationType"
                  :items="regulationTypes"
                  item-text="name"
                  :color="colorTheme"
                  item-value="value"
                  label="Regulation Type"
                  @change="(val) => onChange(val, 'regulationType')"
                />
              </v-flex>
            </v-layout>
            <v-layout
              row
              wrap
            >
              <v-flex
                xs12
                sm3
              >
                <v-text-field
                  v-model="financialProduct.project_name"
                  label="Project Name"
                  :color="colorTheme"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs12
                sm3
              >
                <v-select
                  v-model="financialProduct.investment_broker"
                  :items="investmentBrokers"
                  item-text="name"
                  item-value="value"
                  label="Investment Broker"
                  :color="colorTheme"
                  :readonly="type === 'tranche'"
                  :class="type === 'tranche' ? 'is-readonly' : ''"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs6
                sm2
              >
                <v-select
                  v-model="financialProduct.client_ids"
                  flat
                  :color="colorTheme"
                  small
                  multiple
                  label="Client Ids"
                  :items="clients"
                  @change="onChange"
                />
              </v-flex>
            </v-layout>
            <v-layout
              row
              wrap
            >
              <v-flex
                xs12
                sm3
              >
                <v-text-field
                  v-model="financialProduct.funding_target"
                  label="Funding Target"
                  :color="colorTheme"
                  type="number"
                  :readonly="!isInDraftStage(financialProduct.stage) && type === 'product'"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs6
                sm4
              >
                <v-text-field
                  v-model="financialProduct.notional_value"
                  label="Notional Value"
                  type="number"
                  :color="colorTheme"
                  :readonly="type === 'tranche'"
                  :class="type === 'tranche' ? 'is-readonly' : ''"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs6
                sm2
              >
                <v-text-field
                  v-model="financialProduct.external_project_id"
                  :readonly="type === 'tranche'"
                  :class="type === 'tranche' ? 'is-readonly' : ''"
                  label="External Project Id"
                  type="number"
                  :color="colorTheme"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs6
                sm3
              >
                <v-select
                  v-model="financialProduct.distribution_channel"
                  flat
                  small
                  multiple
                  label="Distribution Channels"
                  :color="colorTheme"
                  :items="distributionChannels"
                  @change="onChange"
                />
              </v-flex>
            </v-layout>
            <v-layout
              row
              wrap
            >
              <v-flex
                xs12
                sm3
              >
                <v-text-field
                  v-model="financialProduct.min_investment_amount"
                  label="Minimum Investment Amount"
                  :color="colorTheme"
                  type="number"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs6
                sm3
              >
                <v-text-field
                  v-model="financialProduct.product_costs"
                  label="Product Costs"
                  :readonly="type === 'tranche'"
                  :class="type === 'tranche' ? 'is-readonly' : ''"
                  type="number"
                  :color="colorTheme"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                xs12
                sm3
              >
                <v-text-field
                  v-model="financialProduct.intermediation_costs"
                  label="Intermediation Costs"
                  type="number"
                  :color="colorTheme"
                  :readonly="type === 'tranche'"
                  :class="type === 'tranche' ? 'is-readonly' : ''"
                  @change="onChange"
                />
              </v-flex>
            </v-layout>
            <v-layout
              v-if="type==='tranche'"
              row
              wrap
            >
              <v-flex
                xs12
                sm4
              >
                <v-select
                  v-model="financialProduct.payment_type"
                  :items="paymentTypes"
                  item-text="name"
                  item-value="value"
                  label="Payment Type"
                  :color="colorTheme"
                  @change="(val) => onChange(val, 'payment_type')"
                />
              </v-flex>
              <template
                v-if="financialProduct.payment_type !== 'mangopay'"
              >
                <v-flex
                  xs12
                  sm4
                >
                  <v-text-field
                    v-model="financialProduct.payment_id"
                    :label="paymentIdLabelByPaymentId(financialProduct.payment_type)"
                    :color="colorTheme"
                    @change="onChange"
                  />
                </v-flex>
              </template>
              <template
                v-else-if="financialProduct.payment_type === 'mangopay'"
              >
                <v-flex
                  xs12
                  sm4
                >
                  <v-text-field
                    v-model="financialProduct.payment_id"
                    label="Mangopay Wallet ID"
                    :color="colorTheme"
                    @change="onChange"
                  />
                </v-flex>
                <v-flex
                  xs12
                  sm4
                >
                  <v-text-field
                    label="Mangopay User (Contractor) ID"
                    :color="colorTheme"
                    :value="getCustomFieldValueByName('mangopay_user_id')"
                    @change="(v) => addOrUpdateCustomField('str', 'mangopay_user_id', v)"
                  />
                </v-flex>
              </template>
            </v-layout>
            <v-layout
              row
              wrap
            >
              <v-flex
                xs6
                sm3
              >
                <custom-field
                  :type="'date'"
                  :color-theme="colorTheme"
                  :value="financialProduct.expected_funding_start"
                  label="Expected Funding Start"
                  :on-change="e => onDatePickerChange(e, 'expected_funding_start')"
                />
              </v-flex>
              <v-flex
                xs6
                sm3
              >
                <v-select
                  v-model="financialProduct.primary_market_settlement"
                  flat
                  small
                  multiple
                  label="Primary Market Settlement"
                  :items="primaryMarketSettlements"
                  :color="colorTheme"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                v-if="type !== 'tranche'"
                xs6
                sm3
              >
                <v-select
                  v-model="financialProduct.secondary_market_enabled"
                  :readonly="!stageIsPublished()"
                  :items="[
                    { text: 'true', value: true },
                    { text: 'false', value: false }
                  ]"
                  name="FP is unlocked for Secondary Market."
                  label="FP is unlocked for Secondary Market."
                  :color="colorTheme"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                v-if="type !== 'tranche'"
                xs6
                sm3
              >
                <custom-field
                  :type="'date'"
                  :color-theme="colorTheme"
                  :value="financialProduct.date_of_loss_certificate"
                  label="Date of Loss Certificate"
                  :on-change="e => onDatePickerChange(e, 'date_of_loss_certificate')"
                />
              </v-flex>
              <v-flex
                v-if="type !== 'tranche'"
                xs6
                sm3
              >
                <v-text-field
                  v-model="financialProduct.spv_wallet_id"
                  label="SPV Wallet ID"
                  :color="colorTheme"
                  @change="onChange"
                />
              </v-flex>
              <v-flex
                v-if="usesTokenInfrastructure() && !isLoanAndEcsp"
                xs6
                sm3
              >
                <v-text-field
                  label="Vault Asset ID"
                  :color="colorTheme"
                  :readonly="type === 'tranche'"
                  :class="type === 'tranche' ? 'is-readonly' : ''"
                  :value="getCustomFieldValueByName('vault_asset_id')"
                  @change="(v) => addOrUpdateCustomField('str', 'vault_asset_id', v)"
                />
              </v-flex>
              <v-flex
                v-else-if="isSubordinatedLoanAndVermaNl || isLoanAndEcsp"
                xs6
                sm3
              >
                <v-text-field
                  label="Vault Asset ID"
                  :color="colorTheme"
                  :value="getCustomFieldValueByName('vault_asset_id')"
                  @change="(v) => addOrUpdateCustomField('str', 'vault_asset_id', v)"
                />
              </v-flex>
            </v-layout>
            <v-layout
              row
              wrap
            >
              <v-flex
                v-if="isSubordinatedLoanAndVermaNl || isLoanAndEcsp"
                xs6
                sm3
              >
                <v-text-field
                  label="Payback Interval In Month"
                  :color="colorTheme"
                  :value="getCustomFieldValueByName('paybackIntervalInMonth')"
                  @change="(v) => addOrUpdateCustomField('str', 'paybackIntervalInMonth', v)"
                />
              </v-flex>
              <v-flex
                v-if="isSubordinatedLoanAndVermaNl || isLoanAndEcsp"
                xs6
                sm3
              >
                <v-text-field
                  label="Running Time In Months"
                  :color="colorTheme"
                  :value="getCustomFieldValueByName('runningTimeInMonths')"
                  @change="(v) => addOrUpdateCustomField('str', 'runningTimeInMonths', v)"
                />
              </v-flex>
              <v-flex
                v-if="isSubordinatedLoanAndVermaNl || isLoanAndEcsp"
                xs6
                sm3
              >
                <custom-field
                  :type="'date'"
                  :color-theme="colorTheme"
                  :value="getCustomFieldValueByName('firstPaybackAt')"
                  label="First Payback At"
                  :on-change="(v) => addOrUpdateCustomField('date', 'firstPaybackAt', v.value)"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-expansion-panel-content>
      <!-- Custom Fields start -->
      <custom-fields-expansion-panel
        :custom-fields="financialProduct.custom_fields"
        :color-theme="colorTheme"
        :add-function="addCustomField"
        :change-function="onCustomFieldChange"
        :delete-function="deleteSelectedFields"
        :instrument="financialProduct.instrument"
      />
      <!-- Custom Fields end -->
      <!-- Issuer start -->
      <issuer-expansion-panel
        :on-value-change="onChange"
        :type="type"
        :issuer="issuer"
        :stage="financialProduct.stage"
        :color-theme="colorTheme"
      />
      <!-- Issuer end -->
      <!-- InterestTerms start -->
      <interest-terms-expansion-panel
        :interest-terms="interestTerms"
        :type="type"
        :stage="financialProduct.stage"
        :color-theme="colorTheme"
      />
      <!-- InterestTerms end -->
      <!-- Tranches start -->
      <v-expansion-panel-content v-if="tranches">
        <template v-slot:header>
          <h6 class="title">
            <v-icon
              class="mr-2"
            >
              view_module
            </v-icon>
            Tranches
            <b>({{ tranches ? tranches.length || 0 : 0 }})</b>
          </h6>
          <div>
            <v-btn
              style="float: right"
              dark
              :color="colorTheme"
              @click.stop="addTrancheModal"
            >
              <v-icon dark>
                add
              </v-icon>
              Add
            </v-btn>
          </div>
        </template>
        <v-data-table
          :headers="tranchesHeaders"
          :items="tranches"
          :pagination.sync="pagination"
          item-key="created_at"
          class="elevation-1"
        >
          <template v-slot:items="props">
            <tr>
              <td class="text-xs-left">
                <DataChip
                  :tooltip-text="props.item.id"
                  :overflow="true"
                  :data="props.item.id"
                />
              </td>
              <td class="text-xs-center">
                {{ props.item.project_name }}
              </td>
              <td class="text-xs-center">
                {{ props.item.funding_target }}
              </td>
              <td class="text-xs-center">
                {{ props.item.primary_market_settlement ? props.item.primary_market_settlement[0] : '' }}
              </td>
              <td class="text-xs-center">
                {{ getDistributionChannels(props.item.distribution_channel) }}
              </td>
              <td class="text-xs-center">
                {{ props.item.stage }}
              </td>
              <td class="text-xs-center">
                {{ props.item.created_at }}
              </td>
              <td class="text-xs-center">
                <v-tooltip
                  top
                  dark
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      color="warning"
                      flat
                      icon
                      v-on="on"
                      @click="editTranche(props.item)"
                    >
                      <v-icon>edit</v-icon>
                    </v-btn>
                  </template>
                  <span>edit</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-expansion-panel-content>
      <!-- Tranches end -->
      <!-- Files start -->
      <files-expansion-panel
        :files="financialProduct.files"
        :color-theme="colorTheme"
        :add-function="addFile"
        :remove-function="removeFile"
        :instrument="financialProduct.instrument"
      />
      <!-- Files end -->
      <!-- DangerZone start -->
      <danger-zone-expansion-panel
        :financial-product="financialProduct"
        :decline-action="decline"
      />
      <!-- DangerZone end -->
    </v-expansion-panel>
    <v-dialog
      v-model="modalOpen"
      width="400"
      persistent
    >
      <v-card>
        <v-card-title :class="colorTheme">
          <span class="white--text">
            Please confirm switching the stage
          </span>
        </v-card-title>
        <v-progress-linear
          v-if="saving"
          :color="colorTheme"
          :indeterminate="true"
          class="ma-0"
        />
        <v-form
          ref="processOrderForm"
        >
          <v-card-actions>
            <v-spacer />
            <v-btn
              flat
              @click="modalOpen = false"
            >
              Close
            </v-btn>
            <e-button
              flat
              type="submit"
              :color="colorTheme"
              text="Confirm"
            />
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <add-tranche
      :dialog="isOpenAddTranche"
      :submit-action="submitAddTranche"
      :cancel-action="closeAddTrancheModal"
      :color-theme="colorTheme"
      :error="error"
      :settlements="financialProduct.primary_market_settlement"
      :distribution-channels="financialProduct.distribution_channel"
      :fp-title="financialProduct.project_name"
    />
    <v-dialog
      v-model="confirmDialogOpen"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">
          {{ dialogType === 'invalidMangopay' ? 'Invalid Mangopay' : 'Confirm Action' }}
        </v-card-title>
        <v-card-text>
          {{ dialogType === 'invalidMangopay'
            ? 'Your Mangopay User ID or Wallet ID is invalid. Do you want to proceed anyway?'
            : 'Are you sure you want to proceed with this action?' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error darken-1"
            text
            @click="_onCancel"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="_onConfirm"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Button } from '@/components/shared';
import PaymentTypesEnum from '../../enum/financialProducts/PaymentTypes';
import InvestmentBrokersEnum from '../../enum/financialProducts/InvestmentBrokers';
import SecurityIdTypesEnum from '../../enum/financialProducts/SecurityIdTypes';
import RegulationTypeEnum from '../../enum/financialProducts/RegulationType';
import PrimaryMarketSettlementEnum from '../../enum/financialProducts/PrimaryMarketSettlements';
import StagesEnum from '../../enum/financialProducts/Stages';
import CustomField from './shared/CustomField';
import AddTranche from './AddTranche';
import CustomFieldsExpansionPanel from './CustomFieldsExpansionPanel';
import FilesExpansionPanel from './FilesExpansionPanel';
import OwnerIdsEnum from '../../enum/financialProducts/OwnerIds';
import DistributionChannelsEnum from '../../enum/financialProducts/DistributionChannels';
import IssuerExpansionPanel from './IssuerExpansionPanel';
import DangerZoneExpansionPanel from './DangerZoneExpansionPanel';
import InterestTermsExpansionPanel from './InterestTermsExpansionPanel';
import StageTransitionButtons from '@/components/financialProducts/StageTransitionButtons';
import { LoanFinancialInstrumentsEnum, FinancialInstrumentsEnum } from '@/enum/financialProducts/FinancialInstruments';
import DataChip from '@/components/shared/DataChip';

export default {
  name: 'FinancialProductDetailComponent',
  components: {
    StageTransitionButtons,
    DangerZoneExpansionPanel,
    IssuerExpansionPanel,
    InterestTermsExpansionPanel,
    FilesExpansionPanel,
    AddTranche,
    CustomField,
    DataChip,
    CustomFieldsExpansionPanel,
    'e-button': Button,
  },
  props: {
    financialProductRaw: {
      type: Object,
      required: true,
    },
    financialProduct: {
      type: Object,
      required: true,
    },
    tranches: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      pagination: {
        page: 1,
        rowsPerPage: 5,
        sortBy: 'created_at',
        descending: true,
      },
      panel: 0,
      isSubordinatedLoanAndVermaNl: false,
      isLoanAndEcsp: false,
      modalOpen: false,
      modalDeclineOpen: false,
      unsavedData: false,
      saving: false,
      switching: false,
      error: '',
      isOpenAddTranche: false,
      startAtPicker: false,
      confirmDialogOpen: false,
      selectedItem: null,
      dialogType: '',
      tranchesHeaders: [
        { text: 'Tranche ID', align: 'left', value: 'id' },
        { text: 'Project Name', align: 'center', value: 'project_name' },
        { text: 'Funding Target', align: 'center', value: 'funding_target' },
        { text: 'Primary Market Settlement', align: 'center', value: 'primary_market_settlement' },
        { text: 'Distribution Channel', align: 'center', value: 'distribution_channel' },
        { text: 'Stage', align: 'center', value: 'stage' },
        { text: 'Created At', align: 'center', value: 'created_at' },
        { text: 'Actions', align: 'center', value: 'edit' },
      ],
    };
  },
  computed: {
    paymentTypes() {
      return Object.keys(PaymentTypesEnum).map(key => ({
        name: PaymentTypesEnum[key],
        value: PaymentTypesEnum[key],
      }));
    },
    investmentBrokers() {
      return Object.keys(InvestmentBrokersEnum).map(key => ({
        name: InvestmentBrokersEnum[key],
        value: InvestmentBrokersEnum[key],
      }));
    },
    securityIdTypes() {
      return Object.keys(SecurityIdTypesEnum).map(key => ({
        name: SecurityIdTypesEnum[key],
        value: SecurityIdTypesEnum[key],
      }));
    },
    regulationTypes() {
      return Object.keys(RegulationTypeEnum).map(key => ({
        name: RegulationTypeEnum[key],
        value: RegulationTypeEnum[key],
      }));
    },
    primaryMarketSettlements() {
      return Object.keys(PrimaryMarketSettlementEnum)
        .map(key => PrimaryMarketSettlementEnum[key]);
    },
    clients() {
      return Object.keys(OwnerIdsEnum).map(key => OwnerIdsEnum[key]);
    },
    distributionChannels() {
      return Object.keys(DistributionChannelsEnum).map(key => DistributionChannelsEnum[key]);
    },
    colorTheme() {
      if (this.type === 'tranche') {
        return 'deep-orange darken-4';
      }

      return 'blue-grey darken-1';
    },
    issuer() {
      return this.getOrAddIssuer();
    },
    interestTerms() {
      return this.getOrAddInterestTerms();
    },
  },
  watch: {
    interestTerms() {
      this.unsavedData = true;
    },
  },
  mounted() {
    this.panel = [true, false];
    if (!this.financialProduct.files) {
      this.financialProduct.files = [];
    }
    if (this.financialProduct.instrument === FinancialInstrumentsEnum.LOAN_2A3A &&
          this.financialProduct.regulationType === RegulationTypeEnum.VermAnl) {
      this.isSubordinatedLoanAndVermaNl = true;
    } else if (this.financialProduct.instrument === FinancialInstrumentsEnum.LOAN_ECSP &&
           this.financialProduct.regulationType === RegulationTypeEnum.Ecsp) {
      this.isLoanAndEcsp = true;
    }
  },
  methods: {
    ...mapActions([
      'updateFinancialProduct',
      'createTranche',
      'declineFP',
      'checkMangoPayUserIdValidity',
      'checkMangoPayWalletValidity',
    ]),
    onChange(value, type) {
      if (type === 'security_id_type') {
        this.deleteCustomFieldByName('vault_asset_id');
        this.deleteCustomFieldByName('smart_contract_address');
        this.financialProduct.security_id = null;
      } else if (type === 'payment_type') {
        this.deleteCustomFieldByName('mangopay_user_id');
      }
      if (this.error && this.error.includes('Mangopay User (Contractor) ID')) {
        this.error = '';
      }

      this.unsavedData = true;
    },
    onDatePickerChange(date, field) {
      this.unsavedData = true;
      this.financialProduct[field] = date.value;
    },
    onCustomFieldChange(data) {
      this.unsavedData = true;
      this.financialProduct.custom_fields[data.index].value = data.value;
    },
    async saveFinancialProductData() {
      try {
        this.saving = true;
        const isMangopaySelected = this.financialProduct.payment_type === 'mangopay';

        if (isMangopaySelected) {
          const mangopayUserIdValid = await this.checkMangoPayUserIdValidity(this.getCustomFieldValueByName('mangopay_user_id'));
          const mangopayWalletValid = await this.checkMangoPayWalletValidity(this.financialProduct.payment_id);

          if (!mangopayUserIdValid || !mangopayWalletValid) {
            this.dialogType = 'invalidMangopay';
            this.confirmDialogOpen = true;
            this.saving = false;
          }
        }

        this.createChangeSet();
        if (this.financialProduct.funding_target && this.financialProduct.notional_value) {
          this.financialProductRaw.existing_pieces = this.financialProduct.funding_target / this.financialProduct.notional_value;
        }
        Object.assign(this.financialProductRaw, { fill_in: this.type });
        await this.updateFinancialProduct(this.financialProductRaw);
        this.unsavedData = false;
        this.modalOpen = false;
        this.saving = false;
        this.error = '';
      } catch (error) {
        this.modalOpen = false;
        this.error = error.response.data.error;
      }
    },

    async _onConfirm() {
      try {
        await this.saveFinancialProductData();
      } catch (error) {
        console.error('Error during confirmation:', error);
      } finally {
        this.confirmDialogOpen = false;
      }
    },
    _onCancel() {
      this.confirmDialogOpen = false;
      this.selectedItem = null;
    },
    createChangeSet() {
      for (const key in this.financialProduct) {
        this.financialProductRaw[key] = this.financialProduct[key];
      }
      this.financialProductRaw.fill_in = this.type;
    },
    addProductInheritedPropertiesToTranche(financialProduct) {
      const issuer = this.getCustomFieldByName('issuer');

      return {
        instrument: financialProduct.instrument,
        security_id_type: financialProduct.security_id_type,
        regulationType: financialProduct.regulationType,
        security_id: financialProduct.security_id,
        interest_terms: financialProduct.interest_terms,
        piece_value: LoanFinancialInstrumentsEnum.includes(financialProduct.instrument) ? 1 : financialProduct.piece_value,
        ...(issuer && {
          issuer: issuer.value,
        }),
        ...(financialProduct.product_costs && {
          product_costs: financialProduct.product_costs,
        }),
        ...(financialProduct.secondary_market_enabled && {
          secondary_market_enabled: financialProduct.secondary_market_enabled,
        }),
        ...(financialProduct.notional_value && {
          notional_value: financialProduct.notional_value,
        }),
        ...(financialProduct.intermediation_costs && {
          intermediation_costs: financialProduct.intermediation_costs,
        }),
        ...(financialProduct.investment_broker && {
          investment_broker: financialProduct.investment_broker,
        }),
        ...(financialProduct.external_project_id && {
          external_project_id: financialProduct.external_project_id,
        }),
      };
    },
    async submitAddTranche(data) {
      const fp = this.financialProduct;
      const tranche = {
        fill_in: 'tranche',
        client_ids: fp.client_ids,
        // Product+Tranche
        ...(data.fundingTarget && {
          funding_target: data.fundingTarget,
        }),
        // Product^Tranche
        ...(data.primaryMarketSettlement && {
          primary_market_settlement: [data.primaryMarketSettlement],
        }),
        ...(data.distributionChannel && {
          distribution_channel: [data.distributionChannel],
        }),
        // Product > Tranche
        expected_funding_start: fp.expected_funding_start,
        project_name: data.title ? data.title : fp.project_name,
        min_investment_amount: fp.min_investment_amount ? fp.min_investment_amount : 0,
        // Product
        ...this.addProductInheritedPropertiesToTranche(fp),
      };
      try {
        const response = await this.createTranche(tranche);
        await this.$router.push(
          // eslint-disable-next-line max-len
          `/financial-products/tranche/${this.financialProduct.instrument}/${this.financialProduct.security_id_type}/${this.financialProduct.security_id}/${response.data.created_at}`,
        );
        this.isOpenAddTranche = false;
        this.$emit('refreshDetailComponent');
      } catch (error) {
        this.error = error.response.data.error;
      }
    },
    async addFile(data) {
      this.financialProduct.files.push({
        category: data.category,
        fileName: data.fileName,
        name: data.name,
        usage: data.usage,
      });
      await this.saveFinancialProductData();
    },
    removeFile(data) {
      this.financialProduct.files = this.financialProduct.files.filter(
        file => file.category !== data.category ||
          file.fileName !== data.fileName ||
          file.name !== data.name ||
          file.usage !== data.usage,
      );
      this.saveFinancialProductData();
    },
    addTrancheModal() {
      this.isOpenAddTranche = true;
    },
    closeAddTrancheModal() {
      this.isOpenAddTranche = false;
    },
    getDistributionChannels(distributionChannels) {
      return distributionChannels ? distributionChannels.join(', ') : '';
    },
    addCustomField(data) {
      if (!this.financialProduct.custom_fields) {
        this.financialProduct.custom_fields = [];
      }

      this.financialProduct.custom_fields.push({
        type: data.type,
        name: data.name,
        value: data.value,
      });
      this.unsavedData = true;
    },
    getCustomFieldByName(name) {
      const customFields = this.financialProduct.custom_fields;
      if (!customFields) {
        return null;
      }

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < customFields.length; i++) {
        const customField = customFields[i];
        if (customField.name === name) {
          return customField;
        }
      }

      return null;
    },
    addOrUpdateCustomField(type, name, value) {
      const customField = this.getCustomFieldByName(name);
      if (customField) {
        customField.type = type;
        if (type === 'str') {
          customField.value = value.trim();
        } else {
          customField.value = value;
        }
      } else {
        this.addCustomField({
          type,
          name,
          value,
        });
      }
      this.unsavedData = true;
      this.$forceUpdate();
    },
    getCustomFieldValueByName(name) {
      const customField = this.getCustomFieldByName(name);
      if (!customField) {
        return '';
      }

      return customField.value;
    },
    deleteSelectedFields(data) {
      for (let i = data.customFields.length - 1; i >= 0; i -= 1) {
        if (data.customFields[i].selected === true) {
          this.deleteCustomFieldByIndex(i);
          this.unsavedData = true;
        }
      }
    },
    deleteCustomFieldByName(name) {
      const customFields = this.financialProduct.custom_fields;
      if (!customFields) {
        return;
      }

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < customFields.length; i++) {
        const customField = customFields[i];
        if (customField.name === name) {
          this.deleteCustomFieldByIndex(i);

          return;
        }
      }
    },
    deleteCustomFieldByIndex(index) {
      this.$delete(this.financialProduct.custom_fields, index);
    },
    async decline() {
      if (this.financialProduct.stage !== 'closed') {
        try {
          const data = {
            instrument: this.financialProduct.instrument,
            security_id_type: this.financialProduct.security_id_type,
            security_id: this.financialProduct.security_id,
          };
          if (this.type === 'tranche') {
            data.created_at = this.financialProduct.created_at;
          }
          await this.declineFP(data);
          this.$emit('refreshDetailComponent');
        } catch (error) {
          this.error = error.response.data.error;
        }
        this.modalDeclineOpen = false;
      }
    },
    async editTranche(tranche) {
      const timestamp = new Date(tranche.created_at).getTime();
      await this.$router.push(`/financial-products/tranche/${tranche.instrument}/${tranche.security_id_type}/${tranche.security_id}/${timestamp}`);
      this.$emit('refreshDetailComponent');
    },
    paymentIdLabelByPaymentId(paymentId) {
      switch (paymentId) {
        case 'bb':
          return 'ISIN';
        case 'secupay':
          return 'Secupay ID';
        default:
          return 'Payment ID';
      }
    },
    isInDraftStage(stage) {
      return stage === 'draft';
    },
    stageIsPublished() {
      return this.financialProduct.stage === StagesEnum.PUBLISHED;
    },
    usesTokenInfrastructure() {
      return this.financialProduct.security_id_type === 'token_symbol'
        || this.financialProduct.instrument === FinancialInstrumentsEnum.LOAN_ECSP;
    },
    getOrAddIssuer() {
      const issuerExists = this.financialProduct.custom_fields
        .filter(customField => customField.name === 'issuer').length !== 0;
      if (!issuerExists) {
        this.addCustomField({
          name: 'issuer',
          type: 'json',
          value: {
            city: undefined,
            creditor_id: undefined,
            iban: undefined,
            name: undefined,
            street: undefined,
            zip: undefined,
            lei: undefined,
          },
        });
      }

      return this.getCustomFieldByName('issuer').value;
    },
    getOrAddInterestTerms() {
      const interestTermsExists = this.financialProduct.interest_terms;
      if (!interestTermsExists) {
        this.financialProduct.interest_terms = {
          profit_calculation_formula: undefined,
          interval: undefined,
          runtime_start: undefined,
          runtime_end_max: undefined,
          runtime_end_min: undefined,
          dates: undefined,
          rates: {
            rate_at_maturity: undefined,
            delay_interest_rate: undefined,
          },
        };
      } else if (!this.financialProduct.interest_terms.rates) {
        this.financialProduct.interest_terms.rates = {
          rate_at_maturity: undefined,
          delay_interest_rate: undefined,
        };
      } else {
        // this has been just added to fix a weird issue with watch not getting triggered when there is a change in interest_terms.Pls do not remove
        JSON.stringify(this.financialProduct.interest_terms);
      }

      return this.financialProduct.interest_terms;
    },
    async onStageTransition(stage) {
      this.switching = true;
      const currentStage = this.financialProduct.stage;
      try {
        this.financialProduct.stage = stage;
        Object.assign(this.financialProduct, { fill_in: this.type });
        this.createChangeSet();
        await this.updateFinancialProduct(this.financialProductRaw);
        this.unsavedData = false;
      } catch (error) {
        this.error = error.response.data.error;
        this.financialProduct.stage = currentStage;
      }
      this.switching = false;
    },
  },
};
</script>

<style>
  .is-readonly {
    opacity: 0.7;
  }
  .is-readonly *:hover {
    cursor: no-drop;
  }
</style>



// WEBPACK FOOTER //
// src/components/financialProducts/FinancialProductDetailComponent.vue